import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useCallback,
} from "react";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { SubscribeDevClient, AuthenticationError } from "@subscribe.dev/client";
import { useSubscribeDevStore, SubscribeDevStore } from "./store";
import { preloadSubscribeDevPermissions } from "./preload";

// User object extracted from JWT token
export type UserObject = {
  userId: string;
  email: string;
  avatarUrl?: string;
};

type SyncStatus = "local" | "syncing" | "synced" | "error";

interface SubscribeDevContextValue {
  client: SubscribeDevClient | null;
  token: string | null;
  user: UserObject | null;
  isSignedIn: boolean;
  queryClient: QueryClient;
  subscriptionModalUrl: string | null;
  setSubscriptionModalUrl: (url: string | null) => void;
  projectToken: string | undefined;
  effectiveApiKey: string | undefined;
  signIn: () => void;
  signOut: () => void;
  store: SubscribeDevStore;
  isDemoMode: boolean;
}

const SubscribeDevContext = createContext<SubscribeDevContextValue | null>(
  null
);

export function useSubscribeDevProvider() {
  const context = useContext(SubscribeDevContext);
  if (!context) {
    throw new Error(
      "useSubscribeDevProvider must be used within SubscribeDevProvider"
    );
  }
  return context;
}

function useSubscribeDevSessionStorage<T>(
  key: string,
  initialValue: T
): [T, (value: T) => void, SyncStatus] {
  const { store } = useSubscribeDevProvider();
  const storage = useStorage();
  const [currentData, setCurrentData] = useState<T>(initialValue);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>("local");
  const [isInitialized, setIsInitialized] = useState(false);

  const client = store.client;
  const isReady = store.isReady;

  useEffect(() => {
    const initializeData = async () => {
      console.log(
        `Initializing storage for key: ${key}, client available: ${!!client}`
      );

      if (!client || !isReady) {
        console.log(`Client not ready yet for key: ${key}`);
        return;
      }

      setSyncStatus("syncing");
      try {
        const storedValue = await storage.get(key);
        console.log(`Retrieved stored value for ${key}:`, storedValue);
        if (storedValue) {
          try {
            console.log("STORED VALUE: ", storedValue);
            const parsed = JSON.parse(storedValue);
            console.log("PARSED DATA: ", parsed);

            // Handle case where data was stored as {key: value} structure
            let dataToSet = parsed;
            if (
              parsed &&
              typeof parsed === "object" &&
              parsed[key] !== undefined
            ) {
              dataToSet = parsed[key];
              console.log("EXTRACTED VALUE FOR KEY: ", key, dataToSet);
            }

            // Validate that parsed data is not corrupted
            if (
              dataToSet === null ||
              dataToSet === undefined ||
              (typeof dataToSet === "string" &&
                dataToSet.includes("[object Object]"))
            ) {
              throw new Error("Corrupted storage data detected");
            }
            setCurrentData(dataToSet);
          } catch (parseError) {
            console.warn(
              `Corrupted storage data for key ${key}, overwriting with initial value:`,
              parseError
            );
            setCurrentData(initialValue);
            console.log("SETTING TO: ", initialValue);
            // Overwrite the bad storage with good initial value
            await storage.set(key, JSON.stringify(initialValue));
            console.log(
              `Overwrote corrupted storage for key ${key} with initial value`
            );
          }
        } else {
          setCurrentData(initialValue);
        }
        setSyncStatus("synced");
      } catch (error) {
        console.error("Failed to initialize data:", error);
        setCurrentData(initialValue);
        setSyncStatus("error");
      }
      setIsInitialized(true);
    };

    if (!isInitialized) {
      initializeData();
    }
  }, [key, initialValue, client, isReady, isInitialized, storage]);

  const setValue = useCallback(
    async (value: T) => {
      setCurrentData(value);

      if (!client || !isReady) {
        console.warn(`setValue: No client available for key ${key}`);
        return;
      }

      setSyncStatus("syncing");

      try {
        await storage.set(key, JSON.stringify(value));
        setSyncStatus("synced");
      } catch (error) {
        console.error("Failed to save data:", error);
        setSyncStatus("error");
      }
    },
    [key, client, isReady, storage]
  );

  return [currentData, setValue, syncStatus];
}

export type UsageInfo = {
  allocatedCredits: number;
  usedCredits: number;
  remainingCredits: number;
  loading: boolean;
  error: string | null;
  refreshUsage: () => Promise<void>;
};

export function useSubscribeDev(): {
  client: SubscribeDevClient | null;
  token: string | null;
  user: UserObject | null;
  isSignedIn: boolean;
  signIn: () => void;
  signOut: () => void;
  subscribe: () => void;
  isSubscribed: boolean;
  subscriptionStatus: any;
  subscriptionLoading: boolean;
  refreshSubscriptionStatus: () => Promise<void>;
  useStorage: <T>(
    key: string,
    initialValue: T
  ) => [T, (value: T) => void, SyncStatus];
  usage: UsageInfo;
  canUseStorage: boolean;
} {
  const { client, token, user, isSignedIn, signIn, signOut, store } =
    useSubscribeDevProvider();
  const {
    subscribe,
    isSubscribed,
    subscriptionStatus,
    subscriptionLoading,
    refreshSubscriptionStatus,
  } = useSubscribeDevAuth();
  const usage = useUsage();

  return {
    client,
    token,
    user,
    isSignedIn,
    signIn,
    signOut,
    subscribe,
    isSubscribed,
    subscriptionStatus,
    subscriptionLoading,
    refreshSubscriptionStatus,
    useStorage: useSubscribeDevSessionStorage,
    usage,
    canUseStorage: store.canUseStorage,
  };
}

function useSubscribeDevAuth() {
  const { store } = useSubscribeDevProvider();
  const [subscriptionStatus, setSubscriptionStatus] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const client = store.client;
  const isSignedIn = store.isSignedIn;
  const token = store.token;
  const isReady = store.isReady;

  const refreshSubscriptionStatus = useCallback(async () => {
    // Only fetch subscription status if we have an access token (state 3)
    if (client && isSignedIn && token && isReady) {
      setLoading(true);
      try {
        const status = await client.getSubscriptionStatus();
        setSubscriptionStatus(status);
      } catch (error) {
        if (error instanceof AuthenticationError) {
          console.log("[SubscribeDev] Token expired (401), signing out automatically");
          store.signOut();
        } else {
          console.error("Failed to refresh subscription status:", error);
        }
      } finally {
        setLoading(false);
      }
    } else {
      setSubscriptionStatus(null);
    }
  }, [client, isSignedIn, token, isReady, store]);

  useEffect(() => {
    if (isReady) {
      refreshSubscriptionStatus();
    }
  }, [refreshSubscriptionStatus, isReady]);

  // Listen for refresh events from subscription modal closure
  useEffect(() => {
    const handleRefresh = () => {
      refreshSubscriptionStatus();
    };

    window.addEventListener("subscribedev:refresh-subscription", handleRefresh);
    return () =>
      window.removeEventListener(
        "subscribedev:refresh-subscription",
        handleRefresh
      );
  }, [refreshSubscriptionStatus]);

  const isSubscribed = subscriptionStatus?.hasActiveSubscription || false;

  const { setSubscriptionModalUrl, effectiveApiKey, projectToken } =
    useSubscribeDevProvider();

  const subscribe = useCallback(async () => {
    if (!client?.pricingUrl) {
      console.warn("No pricing URL.");
      return;
    }

    const url = new URL(client.pricingUrl);

    console.log("Subscribe function called with:", {
      token: token ? token.substring(0, 20) + "..." : "null",
      effectiveApiKey: effectiveApiKey
        ? effectiveApiKey.substring(0, 20) + "..."
        : "null",
      clientHasGetUserToken: !!(client as any)?.getUserToken,
    });

    // Try to get fresh token if we have getUserToken function but no current token
    let currentToken = token;
    if (!currentToken && (client as any)?.getUserToken) {
      try {
        console.log("Attempting to get fresh user token for subscription...");
        currentToken = await (client as any).getUserToken();
        console.log(
          "Got fresh token:",
          currentToken ? currentToken.substring(0, 20) + "..." : "null"
        );
      } catch (error) {
        console.warn("Failed to get fresh user token:", error);
      }
    }

    // Add authentication tokens to URL based on auth type
    const originalParams = store.originalInitParams;

    // Check if this is traditional auth (userToken + projectToken or getUserToken + projectToken)
    // If not traditional auth and we have a token, then it's access token auth
    const isTraditionalAuth =
      (originalParams?.userToken && originalParams?.projectToken) || // Headless auth
      (originalParams?.getUserToken && originalParams?.projectToken); // Async auth
    const isAccessTokenAuth = token && !isTraditionalAuth;

    console.log("Authentication analysis:", {
      isAccessTokenAuth,
      hasEffectiveApiKey: !!effectiveApiKey,
      hasToken: !!token,
      hasCurrentToken: !!currentToken,
      originalAccessToken: originalParams?.accessToken
        ? originalParams.accessToken.substring(0, 10) + "..."
        : "none",
      originalProjectToken: originalParams?.projectToken
        ? originalParams.projectToken.substring(0, 10) + "..."
        : "none",
      effectiveApiKey: effectiveApiKey
        ? effectiveApiKey.substring(0, 10) + "..."
        : "none",
      currentToken: currentToken
        ? currentToken.substring(0, 10) + "..."
        : "none",
    });

    if (isAccessTokenAuth && currentToken) {
      // Access token user - currentToken IS the access token, send it as access_token
      url.searchParams.set("access_token", currentToken);
      console.log("Added access_token to subscription URL (universal auth)");
    } else {
      // Traditional auth - send project token and user token separately
      if (effectiveApiKey) {
        // Strip any padding (=) from the project token before adding to URL
        const strippedToken = effectiveApiKey.replace(/=+$/, '');
        url.searchParams.set("project_token", strippedToken);
        console.log(
          "Added project_token to subscription URL (traditional auth)"
        );
      }

      // Add user token if available (for getUserToken flow)
      if (currentToken) {
        url.searchParams.set("user_token", currentToken);
        console.log("Added user_token to subscription URL");
      } else {
        console.log("No user token available for subscription");
      }
    }

    console.log("Final subscription URL:", url.toString());

    // Check if we're running in an iframe
    const inIframe = window.self !== window.top;

    if (inIframe) {
      // Open popup window for subscription when in iframe
      const popup = window.open(
        url.toString(),
        "subscribe_dev_subscription",
        "width=800,height=700,scrollbars=yes,resizable=yes"
      );

      if (!popup) {
        console.error(
          "Failed to open popup window. Please allow popups for this site."
        );
        // Fallback to modal if popup fails
        setSubscriptionModalUrl(url.toString());
        return;
      }

      // Listen for subscription success/checkout messages from popup
      const handleSubscriptionMessage = (event: MessageEvent) => {
        // Only process messages from the subscription domain
        if (!event.origin || !url.origin.includes(new URL(url).hostname)) {
          return;
        }

        console.log(
          "🔵 POPUP: Received message from subscription popup:",
          event.data
        );

        // Handle subscription success
        if (
          event.data?.type === "REMETRICATE_SUBSCRIPTION_SUCCESS" &&
          event.data?.action === "CLOSE_MODAL"
        ) {
          console.log("🔵 POPUP: Subscription completed successfully");
          popup.close();
          window.removeEventListener("message", handleSubscriptionMessage);
          // Trigger refresh of subscription status
          window.dispatchEvent(
            new CustomEvent("subscribedev:refresh-subscription")
          );
        }

        // Handle checkout redirect - open in new tab
        if (
          event.data?.type === "REMETRICATE_CHECKOUT_REDIRECT" &&
          event.data?.action === "REDIRECT_TO_CHECKOUT"
        ) {
          console.log(
            "🔵 POPUP: Opening Stripe checkout in new tab:",
            event.data.data.checkoutUrl
          );

          const checkoutWindow = window.open(
            event.data.data.checkoutUrl,
            "stripe_checkout",
            "width=800,height=700,scrollbars=yes,resizable=yes"
          );

          if (!checkoutWindow) {
            console.error("🔴 POPUP: Failed to open checkout window");
            alert("Please allow popups to complete your subscription payment.");
            return;
          }

          // Monitor checkout completion
          const checkInterval = setInterval(() => {
            if (checkoutWindow.closed) {
              console.log("🔵 POPUP: Checkout window closed");
              clearInterval(checkInterval);
              // Trigger refresh in case payment completed
              window.dispatchEvent(
                new CustomEvent("subscribedev:refresh-subscription")
              );
              return;
            }

            try {
              const currentUrl = checkoutWindow.location.href;
              if (
                currentUrl.includes("subscription-success") ||
                currentUrl.includes("success") ||
                (currentUrl.includes(window.location.origin) &&
                  !currentUrl.includes("checkout.stripe.com"))
              ) {
                console.log("🔵 POPUP: Checkout completed successfully");
                checkoutWindow.close();
                clearInterval(checkInterval);
                popup.close();
                window.removeEventListener(
                  "message",
                  handleSubscriptionMessage
                );
                window.dispatchEvent(
                  new CustomEvent("subscribedev:refresh-subscription")
                );
              }
            } catch (e) {
              // Cross-origin access blocked - normal while on Stripe's domain
            }
          }, 1000);

          // Clean up after 10 minutes
          setTimeout(
            () => {
              if (!checkoutWindow.closed) {
                checkoutWindow.close();
              }
              clearInterval(checkInterval);
            },
            10 * 60 * 1000
          );
        }
      };

      window.addEventListener("message", handleSubscriptionMessage);

      // Clean up listener if popup is closed manually
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          window.removeEventListener("message", handleSubscriptionMessage);
          clearInterval(checkClosed);
        }
      }, 1000);
    } else {
      // Use iframe modal when not in iframe (existing behavior)
      setSubscriptionModalUrl(url.toString());
    }
  }, [
    client,
    token,
    setSubscriptionModalUrl,
    effectiveApiKey,
    projectToken,
    store,
  ]);

  return {
    isSubscribed,
    subscribe,
    subscriptionStatus,
    subscriptionLoading: loading,
    refreshSubscriptionStatus,
  };
}

function useUsage() {
  const { store, queryClient } = useSubscribeDevProvider();

  const client = store.client;
  const isReady = store.isReady;
  const canFetchUsage = store.canFetchUsage;

  // Use React Query for caching and deduplication
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['subscribe-dev', 'usage', store.token],
    queryFn: async () => {
      if (!client || !isReady || !canFetchUsage) {
        return {
          allocatedCredits: 0,
          usedCredits: 0,
          remainingCredits: 0,
        };
      }

      try {
        const usageData = await client.getUsage();
        const userBalance = usageData.credits.userBalance;
        return {
          allocatedCredits: userBalance?.allocatedCredits || 0,
          usedCredits: userBalance?.usedCredits || 0,
          remainingCredits: userBalance?.remainingCredits || 0,
        };
      } catch (err: any) {
        if (err instanceof AuthenticationError) {
          console.log("[SubscribeDev] Token expired (401), signing out automatically");
          store.signOut();
        } else {
          console.warn("Failed to fetch balance data:", err);
        }
        throw err;
      }
    },
    enabled: !!client && isReady && canFetchUsage,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    refetchOnWindowFocus: true,
  });

  // Listen for usage-changed events from client and invalidate query
  useEffect(() => {
    if (!client || !isReady || !canFetchUsage) return;

    const handleUsageChange = () => {
      console.log("Usage changed, invalidating query...");
      queryClient.invalidateQueries({
        queryKey: ['subscribe-dev', 'usage', store.token]
      });
    };

    client.on("usage-changed", handleUsageChange);

    return () => {
      client.off("usage-changed", handleUsageChange);
    };
  }, [client, isReady, canFetchUsage, queryClient, store.token]);

  const refreshUsage = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    allocatedCredits: data?.allocatedCredits || 0,
    usedCredits: data?.usedCredits || 0,
    remainingCredits: data?.remainingCredits || 0,
    loading: isLoading,
    error: error ? (error as Error).message || "Failed to fetch balance data" : null,
    refreshUsage,
  };
}

function useStorage() {
  const { store } = useSubscribeDevProvider();

  const client = store.client;
  const token = store.token;
  const canUseStorage = store.canUseStorage;
  const isReady = store.isReady;

  const get = async (key: string) => {
    // Only use remote storage if we're ready and allowed to
    if (!client || !isReady || !canUseStorage || !token) {
      const localData = localStorage.getItem(key);
      return localData;
    }

    try {
      console.log(`Getting remote storage for key ${key} to check for updates`);

      // Get the full storage object from the API
      const storageResponse = await client.getStorage();
      const localData = localStorage.getItem(key);

      if (storageResponse?.sessionData?.[key] !== undefined) {
        console.log(`Successfully retrieved remote storage for key ${key}`);
        const remoteValue = storageResponse.sessionData[key];
        // Remote data always overwrites local - aggressive sync
        localStorage.setItem(key, JSON.stringify(remoteValue));
        return JSON.stringify(remoteValue);
      } else {
        // No remote data available - if we have local data but no remote, purge local
        if (localData) {
          console.log(`No remote data for key ${key}, purging local storage`);
          localStorage.removeItem(key);
        }
        console.log(`No data found for key ${key}`);
        return null;
      }
    } catch (error) {
      if (error instanceof AuthenticationError) {
        console.log("[SubscribeDev] Token expired (401), signing out automatically");
        store.signOut();
        return null;
      }
      console.warn(`Failed to get storage for key ${key}:`, error);
      // On any backend error, fall back to local storage
      const localData = localStorage.getItem(key);
      return localData;
    }
  };

  const set = async (key: string, value: string) => {
    // Always set local storage first
    localStorage.setItem(key, value);

    // Only sync to remote storage if we're ready and allowed to
    if (!client || !isReady || !canUseStorage || !token) {
      console.log(`No access token, storing key ${key} locally only`);
      return;
    }

    try {
      // Parse the value to store in sessionData
      let parsedValue;
      try {
        parsedValue = JSON.parse(value);
      } catch {
        parsedValue = value;
      }

      // Get current storage to preserve other keys
      let currentSessionData: Record<string, any> = {};
      try {
        const currentStorage = await client.getStorage();
        currentSessionData = currentStorage?.sessionData || {};
      } catch (error) {
        if (error instanceof AuthenticationError) {
          console.log("[SubscribeDev] Token expired (401), signing out automatically");
          store.signOut();
          return;
        }
        console.warn(
          "Could not get current storage, using empty object:",
          error
        );
      }

      // Update the specific key in sessionData
      const updatedSessionData = {
        ...currentSessionData,
        [key]: parsedValue,
      };

      await client.setStorage(updatedSessionData);
      console.log(`Successfully set remote storage for key ${key}`);
    } catch (error) {
      if (error instanceof AuthenticationError) {
        console.log("[SubscribeDev] Token expired (401), signing out automatically");
        store.signOut();
        return;
      }
      console.warn(
        `Failed to set remote storage for key ${key}, keeping local only:`,
        error
      );
    }
  };

  const remove = async (key: string) => {
    // Always remove from local storage
    localStorage.removeItem(key);

    // Only sync to remote storage if we're ready and allowed to
    if (!client || !isReady || !canUseStorage || !token) return;

    try {
      // Get current storage to preserve other keys
      let currentSessionData: Record<string, any> = {};
      try {
        const currentStorage = await client.getStorage();
        currentSessionData = currentStorage?.sessionData || {};
      } catch (error) {
        if (error instanceof AuthenticationError) {
          console.log("[SubscribeDev] Token expired (401), signing out automatically");
          store.signOut();
          return;
        }
        console.warn("Could not get current storage for removal:", error);
        return;
      }

      // Remove the specific key from sessionData
      const updatedSessionData = { ...currentSessionData };
      delete updatedSessionData[key];

      await client.setStorage(updatedSessionData);
      console.log(`Successfully removed remote storage for key ${key}`);
    } catch (error) {
      if (error instanceof AuthenticationError) {
        console.log("[SubscribeDev] Token expired (401), signing out automatically");
        store.signOut();
        return;
      }
      console.warn(`Failed to remove remote storage for key ${key}:`, error);
    }
  };

  return { get, set, remove };
}

interface SubscriptionModalProps {
  url: string | null;
  onClose: () => void;
  onSubscriptionChange?: () => void;
}

function SubscriptionModal({
  url,
  onClose,
  onSubscriptionChange,
}: SubscriptionModalProps) {
  useEffect(() => {
    if (!url) return;

    const handleMessage = (event: MessageEvent) => {
      console.log("🔵 IFRAME_MODAL: Received message:", event.data);

      // Handle subscription picker checkout redirect
      if (
        event.data?.type === "REMETRICATE_CHECKOUT_REDIRECT" &&
        event.data?.action === "REDIRECT_TO_CHECKOUT"
      ) {
        console.log(
          "🔵 IFRAME_MODAL: Opening Stripe checkout in new tab:",
          event.data.data.checkoutUrl
        );

        // Open Stripe checkout in a new tab/window
        const checkoutWindow = window.open(
          event.data.data.checkoutUrl,
          "stripe_checkout",
          "width=800,height=700,scrollbars=yes,resizable=yes"
        );

        if (!checkoutWindow) {
          console.error(
            "🔴 IFRAME_MODAL: Failed to open checkout window. Please allow popups for this site."
          );
          alert("Please allow popups to complete your subscription payment.");
          return;
        }

        // Monitor the checkout window and listen for completion
        const checkInterval = setInterval(() => {
          // Check if window was closed (user canceled)
          if (checkoutWindow.closed) {
            console.log("🔵 IFRAME_MODAL: Checkout window closed by user");
            clearInterval(checkInterval);
            // Optionally refresh subscription status in case payment completed
            onSubscriptionChange?.();
            return;
          }

          // Try to detect if user returned to success page
          try {
            const currentUrl = checkoutWindow.location.href;
            if (
              currentUrl.includes("subscription-success") ||
              currentUrl.includes("success") ||
              (currentUrl.includes(window.location.origin) &&
                !currentUrl.includes("checkout.stripe.com"))
            ) {
              console.log("🔵 IFRAME_MODAL: Checkout completed successfully");
              checkoutWindow.close();
              clearInterval(checkInterval);
              onSubscriptionChange?.();
              onClose();
            }
          } catch (e) {
            // Cross-origin access blocked - this is normal while on Stripe's domain
            // We'll rely on window.closed detection or manual refresh
          }
        }, 1000);

        // Clean up after 10 minutes to prevent memory leaks
        setTimeout(
          () => {
            if (!checkoutWindow.closed) {
              checkoutWindow.close();
            }
            clearInterval(checkInterval);
          },
          10 * 60 * 1000
        );

        return;
      }

      // Listen for subscription success message from iframe
      if (
        event.data?.type === "REMETRICATE_SUBSCRIPTION_SUCCESS" &&
        event.data?.action === "CLOSE_MODAL"
      ) {
        console.log(
          "🔵 IFRAME_MODAL: Subscription completed successfully:",
          event.data
        );

        // Track successful credit purchase - THIS IS THE MOST IMPORTANT EVENT
        if (typeof window !== 'undefined') {
          // Google Analytics - Standard Purchase Event
          if ((window as any).gtag) {
            (window as any).gtag('event', 'purchase', {
              transaction_id: event.data?.transactionId || Date.now().toString(),
              value: event.data?.value || 0,
              currency: 'USD',
              items: [{
                item_id: 'credits',
                item_name: 'Workflow Credits',
                quantity: 1
              }]
            });
          }

          // Facebook Pixel - Standard Purchase Event
          if ((window as any).fbq) {
            (window as any).fbq('track', 'Purchase', {
              value: event.data?.value || 0,
              currency: 'USD',
              content_type: 'product',
              content_name: 'Workflow Credits'
            });
          }
        }

        onSubscriptionChange?.();
        onClose();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [url, onClose, onSubscriptionChange]);

  if (!url) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        overflow: "hidden",
      }}
      onClick={onClose}
    >
      <style>{`
        :root {
          --max-w: 460px;
        }

        @media (min-width: 1024px) {
          :root {
            --max-w: 1200px;
          }
        }
      `}</style>
      <div
        style={{
          width: "100%",
          height: "100dvh",
          backgroundColor: "#000000a3",
          position: "relative",
          overflow: "hidden",
          margin: "0 auto",
          padding: "7vh 0",
          cursor: "pointer",
          maxHeight: '905px',
        }}
        onClick={onClose}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            bottom: '89%',
            right: "max(calc(-1 * var(--max-w) / 2 + 50vw), 12px)",
            zIndex: 10001,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            color: "white",
            border: "none",
            borderRadius: "50%",
            width: "24px",
            height: "24px",
            cursor: "pointer",
            fontSize: "18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          ×
        </button>
        <iframe
          src={url}
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            overflow: "hidden",
            maxWidth: "var(--max-w)",
            marginInline: "auto",
          }}
          title="Subscription"
        />
      </div>
    </div>
  );
}

interface DemoModeBannerProps {
  isVisible: boolean;
  onPublish: () => void;
  onDismiss: () => void;
}

function DemoModeBanner({
  isVisible,
  onPublish,
  onDismiss,
}: DemoModeBannerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (isVisible && !isDismissed) {
      // Wait 2 seconds before sliding up
      const timer = setTimeout(() => {
        setShouldAnimate(true);
        setIsExpanded(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, isDismissed]);

  const handleDismiss = () => {
    setIsExpanded(false);
    setIsDismissed(true);
    onDismiss();
  };

  const handleExpand = () => {
    setIsExpanded(true);
    setIsDismissed(false);
  };

  if (!isVisible) return null;

  return (
    <>
      {showHelp && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={() => setShowHelp(false)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "500px",
              color: "#1f2937",
              fontFamily: "system-ui, -apple-system, sans-serif",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 16px 0", fontSize: "20px", fontWeight: "600" }}>
              How to disable this banner
            </h3>
            <p style={{ margin: "0 0 12px 0", lineHeight: "1.5" }}>
              To permanently hide this demo banner, add the <code style={{ backgroundColor: "#f3f4f6", padding: "2px 6px", borderRadius: "4px", fontFamily: "monospace" }}>showDemoPublishBanner</code> prop to your SubscribeDevProvider:
            </p>
            <pre
              style={{
                backgroundColor: "#1f2937",
                color: "#e5e7eb",
                padding: "16px",
                borderRadius: "8px",
                overflow: "auto",
                fontSize: "13px",
                fontFamily: "monospace",
                margin: "0 0 16px 0",
              }}
            >
{`<SubscribeDevProvider
  projectToken={token}
  showDemoPublishBanner={false}
>
  <App />
</SubscribeDevProvider>`}
            </pre>
            <button
              onClick={() => setShowHelp(false)}
              style={{
                backgroundColor: "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "6px",
                padding: "10px 20px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                width: "100%",
              }}
            >
              Got it!
            </button>
          </div>
        </div>
      )}
      <div
        style={{
          position: "fixed",
          bottom: isDismissed ? "-60px" : "0",
          left: "50%",
          transform: isDismissed
            ? "translateX(-50%)"
            : `translateX(-50%) translateY(${isExpanded ? "0" : "100%"})`,
          zIndex: 9999,
          backgroundColor: "#3b82f6",
          color: "white",
          padding: "12px 20px",
          boxShadow: "0 -2px 12px rgba(0, 0, 0, 0.2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "12px",
          fontSize: "14px",
          fontFamily: "system-ui, -apple-system, sans-serif",
          transition: shouldAnimate
            ? "transform 0.3s ease-out, bottom 0.3s ease-out"
            : "none",
          borderRadius: isDismissed ? "8px 8px 0 0" : "8px 8px 0 0",
          minWidth: isDismissed ? "40px" : "400px",
          maxWidth: isDismissed ? "40px" : "600px",
        }}
      >
        {isDismissed ? (
        <button
          onClick={handleExpand}
          style={{
            backgroundColor: "transparent",
            color: "white",
            border: "none",
            cursor: "pointer",
            fontSize: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "4px",
            transition: "transform 0.2s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.1)")}
          onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          title="Show demo banner"
        >
          ^
        </button>
      ) : (
        <>
          <span style={{ flex: 1, textAlign: "center" }}>
            You're on a demo app. Press 'Publish' to publish your app.
          </span>
          <button
            onClick={onPublish}
            style={{
              backgroundColor: "white",
              color: "#3b82f6",
              border: "none",
              borderRadius: "6px",
              padding: "6px 16px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          >
            Publish
          </button>
          <button
            onClick={() => setShowHelp(true)}
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.15)",
              color: "white",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              borderRadius: "6px",
              padding: "6px 12px",
              fontSize: "13px",
              fontWeight: "500",
              cursor: "pointer",
              transition: "background-color 0.2s",
              whiteSpace: "nowrap",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor =
                "rgba(255, 255, 255, 0.25)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor =
                "rgba(255, 255, 255, 0.15)")
            }
          >
            Hide Banner?
          </button>
          <button
            onClick={handleDismiss}
            style={{
              backgroundColor: "transparent",
              color: "white",
              border: "none",
              borderRadius: "50%",
              width: "24px",
              height: "24px",
              cursor: "pointer",
              fontSize: "18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              transition: "background-color 0.2s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor =
                "rgba(255, 255, 255, 0.2)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "transparent")
            }
          >
            ×
          </button>
        </>
      )}
      </div>
    </>
  );
}

interface SubscribeDevProviderProps {
  children: React.ReactNode;
  projectToken?: string;
  project?: string;
  baseUrl?: string;
  authorizationUrl?: string;
  accessToken?: string;
  autoSignIn?: boolean;
  getUserToken?: () => Promise<string>;
  onPublish?: () => void;
  showDemoPublishBanner?: boolean;
}

export function SubscribeDevProvider({
  children,
  projectToken,
  project,
  baseUrl,
  authorizationUrl,
  accessToken,
  autoSignIn,
  getUserToken,
  onPublish,
  showDemoPublishBanner = true,
}: SubscribeDevProviderProps) {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5, // 5 minutes
        refetchOnWindowFocus: false,
      },
    },
  });

  return (
    <QueryClientProvider client={queryClient}>
      <SubscribeDevProviderInner
        projectToken={projectToken}
        projectSlug={project}
        queryClient={queryClient}
        baseUrl={baseUrl}
        authorizationUrl={authorizationUrl}
        accessToken={accessToken}
        autoSignIn={autoSignIn}
        getUserToken={getUserToken}
        onPublish={onPublish}
        showDemoPublishBanner={showDemoPublishBanner}
      >
        {children}
      </SubscribeDevProviderInner>
    </QueryClientProvider>
  );
}

function SubscribeDevProviderInner({
  children,
  projectToken,
  projectSlug,
  queryClient,
  baseUrl,
  authorizationUrl,
  accessToken,
  autoSignIn,
  getUserToken,
  onPublish,
  showDemoPublishBanner = true,
}: {
  children: React.ReactNode;
  projectToken?: string;
  projectSlug?: string;
  queryClient: QueryClient;
  baseUrl?: string;
  authorizationUrl?: string;
  accessToken?: string;
  autoSignIn?: boolean;
  getUserToken?: () => Promise<string>;
  onPublish?: () => void;
  showDemoPublishBanner?: boolean;
}) {
  const store = useSubscribeDevStore();
  const [showDemoBanner, setShowDemoBanner] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Determine if we're in demo mode (no projectToken and no projectSlug)
  const isDemoMode = !projectToken && !projectSlug;

  // Initialize the store when the component mounts or props change
  useEffect(() => {
    store.initializeAuth({
      projectToken,
      baseUrl,
      authorizationUrl,
      accessToken,
      autoSignIn,
      getUserToken,
    });
  }, [projectToken, baseUrl, authorizationUrl, accessToken, autoSignIn, getUserToken]);

  // Show demo banner when user signs in in demo mode
  useEffect(() => {
    if (showDemoPublishBanner && isDemoMode && store.isSignedIn && !bannerDismissed) {
      setShowDemoBanner(true);
    } else {
      setShowDemoBanner(false);
    }
  }, [showDemoPublishBanner, isDemoMode, store.isSignedIn, bannerDismissed]);

  // Preload permissions when user signs in (only in sandbox environments)
  useEffect(() => {
    if (store.isSignedIn) {
      preloadSubscribeDevPermissions().catch((err) => {
        console.debug("[SubscribeDev] Preload failed (non-critical):", err);
      });
    }
  }, [store.isSignedIn]);

  const handleModalClose = () => {
    store.setSubscriptionModalUrl(null);
    // Trigger a refresh of subscription status across the app
    window.dispatchEvent(new CustomEvent("subscribedev:refresh-subscription"));
  };

  const handlePublish = () => {
    console.log("[SubscribeDev] handlePublish called", {
      hasOnPublish: !!onPublish,
      isDemoMode,
    });

    if (onPublish) {
      onPublish();
    } else {
      // Default behavior: redirect to platform create project page
      const currentHost = window.location.host;
      let platformUrl = null;

      if (
        currentHost.includes("localhost") ||
        currentHost.includes("127.0.0.1")
      ) {
        platformUrl = "http://localhost:3000";
      } else if (currentHost.includes("subscribe-dev.com")) {
        platformUrl = "https://platform.subscribe-dev.com";
      } else if (currentHost.includes("subscribe.dev")) {
        platformUrl = "https://platform.subscribe.dev";
      }

      if (platformUrl) {
        // Open platform in new tab with create=true to open the create project dialog
        const redirectUrl = `${platformUrl}/?create=true`;
        console.log("[SubscribeDev] Opening in new tab:", redirectUrl);
        window.open(redirectUrl, "_blank", "noopener,noreferrer");
      } else {
        console.error(
          "[SubscribeDev] Could not determine platform URL from host:",
          currentHost
        );
      }
    }
  };

  const handleBannerDismiss = () => {
    setBannerDismissed(true);
  };

  // signIn function that redirects to authorization URL or opens popup if in iframe
  // Also checks for access_token in query params and uses that if present
  const signIn = useCallback(() => {
    // First check if store has a deferred token - if so, use store.signIn() which will handle it
    if (store.deferredAccessToken) {
      console.log('[SubscribeDev] Deferred token available, using store.signIn()');
      store.signIn();
      return;
    }

    if (!store.client) return;

    // Check if there's an access_token in the query params
    const params = new URLSearchParams(window.location.search);
    const accessTokenFromQuery = params.get('access_token');

    if (accessTokenFromQuery) {
      console.log('[SubscribeDev] Found access_token in query params, signing in with it...');
      // Use the access token to sign in
      store.handleAuthCallback(accessTokenFromQuery);

      // Clean up the URL by removing the access_token parameter
      params.delete('access_token');
      const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}${window.location.hash}`;
      window.history.replaceState({}, '', newUrl);
      return;
    }

    // No query param token, proceed with normal auth flow
    // For slug-based auth, get base authorizationUrl without /demo path
    let authUrl = store.client.authorizationUrl;
    if (projectSlug && authUrl.endsWith('/demo')) {
      authUrl = authUrl.slice(0, -5); // Remove /demo suffix
    }
    const redirectUrl = new URL(authUrl);

    // Handle slug-based or token-based authentication
    if (projectSlug) {
      // Slug-based auth: use explicit /project/{slug} path
      // e.g., https://auth.subscribe.dev/project/my-project-slug
      redirectUrl.pathname = `/project/${projectSlug}`;
      // Don't add project_token query param for slug-based auth
    } else if (projectToken) {
      // Token-based auth: add project_token as query parameter (existing behavior)
      // Strip any padding (=) from the project token before adding to URL
      const strippedToken = projectToken.replace(/=+$/, '');
      redirectUrl.searchParams.set("project_token", strippedToken);
    }

    // Add return URL
    redirectUrl.searchParams.set("redirect_uri", window.location.href);

    // Check if we're running in an iframe
    const inIframe = window.self !== window.top;

    if (inIframe) {
      // Open popup window for auth
      const popup = window.open(
        redirectUrl.toString(),
        "subscribe_dev_auth",
        "width=600,height=700,scrollbars=yes,resizable=yes"
      );

      if (!popup) {
        console.error(
          "Failed to open popup window. Please allow popups for this site."
        );
        return;
      }

      // Listen for auth success message from popup
      const handleAuthMessage = (event: MessageEvent) => {
        if (event.origin !== new URL(store.client!.authorizationUrl).origin) {
          return;
        }

        if (event.data?.type === "REMETRICATE_AUTH_SUCCESS") {
          const { access_token } = event.data;
          if (access_token) {
            // Update the store with the new token
            store.handleAuthCallback(access_token);
            popup.close();
          }
          window.removeEventListener("message", handleAuthMessage);
        }
      };

      window.addEventListener("message", handleAuthMessage);

      // Clean up listener if popup is closed manually
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          window.removeEventListener("message", handleAuthMessage);
          clearInterval(checkClosed);
        }
      }, 1000);
    } else {
      // Normal redirect behavior when not in iframe
      window.location.href = redirectUrl.toString();
    }
  }, [store, projectToken, projectSlug]);

  // Calculate effective API key for context
  const effectiveApiKey = projectToken;

  const contextValue: SubscribeDevContextValue = {
    client: store.client,
    token: store.token,
    user: store.user,
    isSignedIn: store.isSignedIn,
    queryClient,
    subscriptionModalUrl: store.subscriptionModalUrl,
    setSubscriptionModalUrl: store.setSubscriptionModalUrl,
    projectToken,
    effectiveApiKey,
    signIn,
    signOut: store.signOut,
    store,
    isDemoMode,
  };

  return (
    <SubscribeDevContext.Provider value={contextValue}>
      {children}
      <DemoModeBanner
        isVisible={showDemoBanner}
        onPublish={handlePublish}
        onDismiss={handleBannerDismiss}
      />
      <SubscriptionModal
        url={store.subscriptionModalUrl}
        onClose={handleModalClose}
        onSubscriptionChange={() =>
          window.dispatchEvent(
            new CustomEvent("subscribedev:refresh-subscription")
          )
        }
      />
    </SubscribeDevContext.Provider>
  );
}

// Export preload utilities for manual use if needed
export { preloadSubscribeDevPermissions, isInSandbox } from "./preload";
